// libraries
import React, { useMemo, useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { Box, InputAdornment, Grid, Stack, Button, Typography } from '@mui/material'
import { useTheme } from '@mui/styles'
import { useTranslation } from 'react-i18next'

// specific components
import ScaleImageCell from 'components/containers/inbound-tickets/components/ScaleImageCell'

// generic components
import Input from 'components/generic/form/Input'
import ScalePopover from 'components/generic/popovers/ScalePopover'

// helpers and utils
import { GS_ROLE, IMAGE_TYPES } from 'utils/constants'
import { generateRequestImagesFunction } from 'utils/images'
import {
  INPUT_FOCUS_INDEX,
  onKeyPressNumberField,
  buttonDisabled,
  getInputButtons,
  onClickEntryButton,
  onPressTab,
} from 'components/containers/inbound-tickets/helpers/materialEntryFormHelpers'
import { isManualValidationInfo } from 'components/containers/inbound-tickets/helpers/grid-validations'
import { useScaleState } from 'utils/custom-hooks/useScaleState'

const MaterialEntryFormTruckWeights = ({
  validateIsManual,
  inputFocusIndex,
  setInputFocusIndex,
  onDone,
  setMultiImageBladeData,
  onClickAxleWeightsButton,
  setGrossScaleButtonClicked,
  setTareScaleButtonClicked,
}) => {
  const { t } = useTranslation('inboundTicket')
  const theme = useTheme()

  const { control, setValue, getValues, watch } = useFormContext()

  const { selectedCameraIds, selectedScaleIds, scaleCameras } = useSelector(state => state.devices)
  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals, flagManuallyEnteredWeights, enableAxleWeights } =
    settings?.purchaseSettings?.inboundTickets || {}
  const { canFlagManualScaleReadOverrideFF } = useSelector(state => state.featureFlags?.variables || {})
  const canFlagManualScaleReadOverride = canFlagManualScaleReadOverrideFF && flagManuallyEnteredWeights

  const isNativeCamera =
    selectedCameraIds?.length === 0 && Object.values(scaleCameras || {}).every(cameras => !cameras?.length)
  const { requestImages } = generateRequestImagesFunction()

  const { isFetchingScaleReading, showScaleButton, readScale } = useScaleState()
  const [scalePopoverData, setScalePopoverData] = useState({})

  const truckGrossEl = useRef(null)
  const truckTareEl = useRef(null)

  const callRequestImages = (images, path) => {
    if ((images || []).length === 0 && !isNativeCamera) {
      requestImages(imageData => {
        setValue(path, imageData, { shouldDirty: true })
      })
    }
  }

  const callReadScale = ({ weightType, scaleId = '' }) => {
    readScale(scaleData => {
      const isGross = weightType === 'gross'
      const inputEl = isGross ? truckGrossEl : truckTareEl

      setValue(`truck.${weightType}`, scaleData.weight)
      inputEl?.current?.focus()
      setInputFocusIndex(INPUT_FOCUS_INDEX[isGross ? 'GROSS' : 'TARE'])
      if (canFlagManualScaleReadOverride) {
        if (isGross) setGrossScaleButtonClicked(true)
        else setTareScaleButtonClicked(true)
      }
    }, scaleId)
  }

  const scaleButtonClickHandler = (weightType, event) => {
    if (!['gross', 'tare'].includes(weightType)) throw new Error('Invalid weight type!')

    if (selectedScaleIds?.length > 1) {
      setScalePopoverData({
        anchorEl: event.target,
        weightType,
      })
    } else {
      callReadScale({ weightType })
    }
  }

  const { roles } = useSelector(state => state.permissions)

  const hasEditPermission = !!roles?.find(
    r =>
      r.roleName === GS_ROLE.YARD_ADMIN || r.roleName === GS_ROLE.SCALE_OPERATOR || r.roleName === GS_ROLE.YARD_MANAGER,
  )

  const isManualGross = getValues('truck.isManualGross')
  const isManualTare = getValues('truck.isManualTare')
  const truckGrossImages = watch('truck.grossImages')
  const truckTareImages = watch('truck.tareImages')

  const truckFieldList = [
    {
      index: INPUT_FOCUS_INDEX.GROSS,
      ref: truckGrossEl,
      fieldName: 'truck.gross',
    },
    {
      index: INPUT_FOCUS_INDEX.TARE,
      ref: truckTareEl,
      fieldName: 'truck.tare',
    },
  ]

  const currentTruckGross = watch('truck.gross')
  const [grossTruckFieldColor, setGrossTruckFieldColor] = useState(null)

  useEffect(() => {
    if (canFlagManualScaleReadOverride) {
      const isManual = validateIsManual(currentTruckGross, 'gross')
      const { color } = isManualValidationInfo(isManual, canFlagManualScaleReadOverride)
      setGrossTruckFieldColor(color)
      setValue('truck.isManualGross', isManual)
    }
  }, [currentTruckGross])

  const currentTruckTare = watch('truck.tare')
  const [tareTruckFieldColor, setTareTruckFieldColor] = useState(null)

  useEffect(() => {
    if (canFlagManualScaleReadOverride) {
      const isManual = validateIsManual(currentTruckTare, 'tare')
      const { color } = isManualValidationInfo(isManual, canFlagManualScaleReadOverride)
      setTareTruckFieldColor(color)
      setValue('truck.isManualTare', isManual)
    }
  }, [currentTruckTare])

  const axleWeights = watch('truck.axleWeights')

  const inputButtons = useMemo(() => {
    return getInputButtons(defaultYardUm, enableAxleWeights, (axleWeights || [])?.length > 1)
  }, [defaultYardUm, enableAxleWeights, axleWeights])

  const removeAxleWeights = () => setValue('truck.axleWeights', [])

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} mt={2} spacing={3}>
      <ScalePopover
        scalePopoverData={scalePopoverData}
        setScalePopoverData={setScalePopoverData}
        callReadScale={callReadScale}
      />
      <Box width={{ xs: '100%', md: '40%' }}>
        <Stack spacing={2}>
          <Typography>{t('truck_total')}</Typography>
          <Stack direction="row" columnGap={1}>
            <Input
              autoFocus
              name="truck.gross"
              label={t('gross')}
              data-test="truck-gross"
              variant="outlined"
              defaultValue=""
              fullWidth
              color={grossTruckFieldColor}
              focused={canFlagManualScaleReadOverride && isManualGross}
              disabled={isFetchingScaleReading || !hasEditPermission}
              control={control}
              inputRef={truckGrossEl}
              onChange={() => {
                setValue('truck.axleWeights', [])
                callRequestImages(truckGrossImages, 'truck.grossImages')
              }}
              onMouseDown={() => setInputFocusIndex(INPUT_FOCUS_INDEX.GROSS)}
              onKeyDown={event =>
                onPressTab({
                  event,
                  fieldList: truckFieldList,
                  inputFocusIndex,
                  setInputFocusIndex,
                  isAdminOrScaleOp: hasEditPermission,
                })
              }
              inputProps={{ inputMode: 'none' }}
              InputProps={{
                endAdornment: <InputAdornment position="end">{defaultYardUm}</InputAdornment>,
                onKeyPress: event => onKeyPressNumberField(event, { showWeightDecimals }),
              }}
            />
            {showScaleButton ? (
              <Button
                name="read-scale"
                variant="outlined"
                size="small"
                onClick={event => {
                  setValue('truck.axleWeights', [])
                  scaleButtonClickHandler('gross', event)
                  callRequestImages(truckGrossImages, 'truck.grossImages')
                }}
              >
                {t('take_weight')}
              </Button>
            ) : (
              ''
            )}
          </Stack>
          <Stack direction="row" columnGap={1}>
            <Input
              name="truck.tare"
              label={t('tare')}
              data-test="truck-tare"
              variant="outlined"
              defaultValue=""
              fullWidth
              color={tareTruckFieldColor}
              focused={canFlagManualScaleReadOverride && isManualTare}
              disabled={isFetchingScaleReading || !hasEditPermission}
              control={control}
              inputRef={truckTareEl}
              onChange={() => {
                setValue('truck.axleWeights', [])
                callRequestImages(truckTareImages, 'truck.tareImages')
              }}
              onMouseDown={() => setInputFocusIndex(INPUT_FOCUS_INDEX.TARE)}
              onKeyDown={event =>
                onPressTab({
                  event,
                  fieldList: truckFieldList,
                  inputFocusIndex,
                  setInputFocusIndex,
                  isAdminOrScaleOp: hasEditPermission,
                })
              }
              inputProps={{ inputMode: 'none' }}
              InputProps={{
                endAdornment: <InputAdornment position="end">{defaultYardUm}</InputAdornment>,
                onKeyPress: event => onKeyPressNumberField(event, { showWeightDecimals }),
              }}
            />
            {showScaleButton ? (
              <Button
                name="read-scale"
                variant="outlined"
                size="small"
                onClick={event => {
                  setValue('truck.axleWeights', [])
                  scaleButtonClickHandler('tare', event)
                  callRequestImages(truckTareImages, 'truck.tareImages')
                }}
              >
                {t('take_weight')}
              </Button>
            ) : (
              ''
            )}
          </Stack>
          <Stack direction="row" sx={{ flex: 2 }} spacing={1}>
            <ScaleImageCell
              filename={(truckGrossImages || [])[0]?.imageFilename}
              imageType={IMAGE_TYPES.GROSS}
              isNativeCamera={isNativeCamera}
              hasAxleWeights={getValues('truck')?.axleWeights?.length}
              onRetake={() => {
                requestImages(imageData => {
                  setValue('truck.grossImages', imageData, {
                    shouldDirty: true,
                  })
                })
              }}
              onClick={() => {
                const truckFormData = getValues('truck')
                const hasAxleWeights = truckFormData?.axleWeights?.length

                const newMultiImageBladeData = {
                  ...truckFormData,
                  id: 'truck-total',
                }
                if (hasAxleWeights) {
                  newMultiImageBladeData.axleNumber = 1
                }
                if (truckFormData?.grossImages?.length > 0 || isNativeCamera || hasAxleWeights) {
                  setMultiImageBladeData(newMultiImageBladeData)
                }
              }}
            />
            <ScaleImageCell
              filename={(truckTareImages || [])[0]?.imageFilename}
              imageType={IMAGE_TYPES.TARE}
              isNativeCamera={isNativeCamera}
              hasAxleWeights={getValues('truck')?.axleWeights?.length}
              onRetake={() => {
                requestImages(imageData => {
                  setValue('truck.tareImages', imageData, {
                    shouldDirty: true,
                  })
                })
              }}
              onClick={() => {
                const truckFormData = getValues('truck')
                const hasAxleWeights = truckFormData?.axleWeights?.length

                const newMultiImageBladeData = {
                  ...truckFormData,
                  id: 'truck-total',
                }
                if (hasAxleWeights) {
                  newMultiImageBladeData.axleNumber = 1
                }
                if (truckFormData?.tareImages?.length > 0 || isNativeCamera || hasAxleWeights) {
                  setMultiImageBladeData(newMultiImageBladeData)
                }
              }}
            />
          </Stack>
        </Stack>
      </Box>
      <Box width={{ xs: '100%', md: '60%' }}>
        <Grid container spacing={1} sx={{ textAlign: 'center', flex: 1 }}>
          {inputButtons.map(button => (
            <Grid key={button.id} item xs={button.id === 'buttonDot' || button.id === 'buttonHyphen' ? 1.5 : 3}>
              <Button
                variant="default"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 1,
                  borderColor: 'grey.400',
                  borderRadius: 1,
                  backgroundColor:
                    button.id === 'buttonDone' ? theme.palette.primary.main : theme.palette.background.default,
                  height: 90,
                  '&:hover': {
                    backgroundColor: button.id === 'buttonDone' && theme.palette.primary.dark,
                  },
                }}
                fullWidth
                disabled={buttonDisabled(button, inputFocusIndex, {
                  startIndex: INPUT_FOCUS_INDEX.GROSS,
                  endIndex: INPUT_FOCUS_INDEX.TARE,
                  showWeightDecimals,
                  updateWeightPermission: hasEditPermission,
                })}
                onClick={() => {
                  onClickEntryButton(
                    button,
                    inputFocusIndex,
                    setInputFocusIndex,
                    truckFieldList,
                    onDone,
                    setValue,
                    hasEditPermission,
                    onClickAxleWeightsButton,
                    removeAxleWeights,
                  )
                  if (Number(button?.value)) {
                    const { fieldName } = truckFieldList.find(field => field.index === inputFocusIndex) || {}
                    switch (fieldName) {
                      case 'truck.gross':
                        callRequestImages(truckGrossImages, 'truck.grossImages')
                        break
                      case 'truck.tare':
                        callRequestImages(truckTareImages, 'truck.tareImages')
                        break
                      default:
                        break
                    }
                  }
                }}
              >
                {button.content}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  )
}

MaterialEntryFormTruckWeights.propTypes = {}

MaterialEntryFormTruckWeights.defaultProps = {}

export default MaterialEntryFormTruckWeights
